import axios from 'axios';
import { i18n } from './i18n';
import { getShareAttribution } from './attribution';
import { openCartDrawer } from './cartDrawer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const sessionId = (() => { try { let s = localStorage.getItem('otp_sid'); if (!s) { s = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`); localStorage.setItem('otp_sid', s); } return s; } catch (e) { return ''; } })();

export const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
    ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Expired / invalid session: clear it and send the user back to login
api.interceptors.response.use(
  (response) => { window.dispatchEvent(new CustomEvent('api-status', { detail: { down: false } })); return response; },
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    // FastAPI 422 : detail est une liste d'objets {type, loc, msg…} → texte lisible (sinon React plante en l'affichant)
    const detail = error.response?.data?.detail;
    const frMsg = (m = '') => m
      .replace(/^String should have at most (\d+) characters?$/, '$1 caractères maximum')
      .replace(/^String should have at least (\d+) characters?$/, '$1 caractères minimum')
      .replace(/^List should have at most (\d+) items?.*$/, '$1 éléments maximum')
      .replace(/^Field required$/, 'champ obligatoire')
      .replace(/^Input should be greater than or equal to (\S+)$/, 'minimum $1')
      .replace(/^Input should be less than or equal to (\S+)$/, 'maximum $1')
      .replace(/^Input should be a valid .*$/, 'valeur invalide');
    if (Array.isArray(detail)) {
      error.response.data.detail_raw = detail;
      error.response.data.detail = detail.map((d) => (typeof d === 'string' ? d : `${(d.loc || []).filter((x) => x !== 'body' && x !== 'query').join('.')}${d.loc?.length ? ' : ' : ''}${frMsg(d.msg)}`)).join(' · ') || 'Données invalides';
    } else if (detail && typeof detail === 'object') {
      error.response.data.detail_raw = detail;
      error.response.data.detail = detail.msg || detail.message || JSON.stringify(detail);
    }
    if (!error.response && error.code !== 'ERR_CANCELED') {
      window.dispatchEvent(new CustomEvent('api-status', { detail: { down: true, reason: 'network' } }));
    } else if (status >= 502 && status <= 504) {
      window.dispatchEvent(new CustomEvent('api-status', { detail: { down: true, reason: 'server' } }));
    }
    if (status === 401 && !url.includes('/auth/login') && !url.includes('/auth/register') && localStorage.getItem('token')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const current = window.location.pathname + window.location.search;
      if (!current.startsWith('/connexion')) {
        window.location.href = `/connexion?expired=1&redirect=${encodeURIComponent(current)}`;
      }
    }
    return Promise.reject(error);
  }
);

const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp ? payload.exp * 1000 < Date.now() : false;
  } catch {
    return true;
  }
};

// Auth API
const mergeGuestWishlist = async () => {
  const { wishlistStore } = await import('./wishlistStore');
  await wishlistStore.mergeGuest();
};

const stockError = (message) => Object.assign(new Error(message), { isStockError: true });

export const authAPI = {
  register: async (userData) => {
    const response = await api.post('/auth/register', { lang: i18n.get(), ...userData });
    return response.data;
  },

  forgotPassword: async (email) => (await api.post('/auth/forgot-password', { email, lang: i18n.get() })).data,
  checkResetToken: async (token) => (await api.get(`/auth/reset-password/${encodeURIComponent(token)}`)).data,
  resetPassword: async (token, newPassword) => {
    const response = await api.post('/auth/reset-password', { token, new_password: newPassword });
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      notifyCartChanged();
    }
    return response.data;
  },
  
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      try { await cartAPI.mergeGuestCart(); } catch (e) { console.error('Cart merge failed', e); }
      try { await mergeGuestWishlist(); } catch (e) { console.error('Wishlist merge failed', e); }
      notifyCartChanged();
    }
    return response.data;
  },
  
  logout: () => {
    if (localStorage.getItem('token') && !localStorage.getItem('impersonation')) api.delete('/auth/sessions/current').catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('impersonation');
    localStorage.removeItem('admin_backup');
    localStorage.removeItem('referralCode');
    window.dispatchEvent(new Event('wishlist-changed'));
  },

  // Admin "log in as customer": swap the session, keep the admin session aside to come back to it
  impersonate: async (userId) => {
    const { data } = await api.post(`/admin/customers/${userId}/impersonate`);
    localStorage.setItem('admin_backup', JSON.stringify({ token: localStorage.getItem('token'), user: localStorage.getItem('user') }));
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('user', JSON.stringify(data.user));
    localStorage.setItem('impersonation', JSON.stringify({ admin: data.impersonated_by, customer: data.user, until: Date.now() + data.expires_in_minutes * 60000 }));
    notifyCartChanged();
    window.dispatchEvent(new Event('wishlist-changed'));
    return data;
  },
  impersonation: () => { try { return JSON.parse(localStorage.getItem('impersonation') || 'null'); } catch { return null; } },
  stopImpersonation: () => {
    const backup = JSON.parse(localStorage.getItem('admin_backup') || 'null');
    localStorage.removeItem('impersonation');
    localStorage.removeItem('admin_backup');
    if (backup?.token) { localStorage.setItem('token', backup.token); localStorage.setItem('user', backup.user); } else { localStorage.removeItem('token'); localStorage.removeItem('user'); }
    notifyCartChanged();
    window.dispatchEvent(new Event('wishlist-changed'));
  },
  
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  docLang: () => { try { return JSON.parse(localStorage.getItem('user') || '{}').doc_lang || null; } catch { return null; } },
  setDocLang: async (lang) => {
    const response = await api.put('/auth/doc-lang', { lang });
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    localStorage.setItem('user', JSON.stringify({ ...stored, doc_lang: response.data.doc_lang }));
    return response.data;
  },
  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  updateProfile: async (data) => {
    const response = await api.put('/auth/profile', data);
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    localStorage.setItem('user', JSON.stringify({ ...stored, first_name: response.data.first_name, last_name: response.data.last_name, doc_lang: response.data.doc_lang }));
    return response.data;
  },

  changePassword: async (current_password, new_password) => {
    const response = await api.put('/auth/password', { current_password, new_password });
    if (response.data?.access_token) localStorage.setItem('token', response.data.access_token); // other devices are revoked, this one stays signed in
    return response.data;
  },
  setShipCountry: async (country) => (await api.put('/auth/ship-country', { country })).data,
  sessions: async () => (await api.get('/auth/sessions')).data,
  revokeSession: async (id) => (await api.delete(`/auth/sessions/${id}`)).data,
  revokeOtherSessions: async () => (await api.post('/auth/sessions/revoke-all')).data,
  
  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },
  
  isAuthenticated: () => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    if (isTokenExpired(token)) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return false;
    }
    return true;
  },

  isAdmin: () => {
    if (!authAPI.isAuthenticated()) return false;
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return !!user?.is_admin;
  }
};

// Products API
export const crossSellAPI = {
  recommend: async (body) => (await api.post('/cross-sell/recommend', body)).data,
  event: (body) => api.post('/cross-sell/events', body).catch(() => {}),
  rules: async () => (await api.get('/admin/cross-sell/rules')).data,
  createRule: async (body) => (await api.post('/admin/cross-sell/rules', body)).data,
  updateRule: async (id, body) => (await api.put(`/admin/cross-sell/rules/${id}`, body)).data,
  deleteRule: async (id) => (await api.delete(`/admin/cross-sell/rules/${id}`)).data,
  setSettings: async (body) => (await api.put('/admin/cross-sell/settings', body)).data,
  stats: async (days = 30) => (await api.get('/admin/cross-sell/stats', { params: { days } })).data,
  seedDefaults: async () => (await api.post('/admin/cross-sell/seed-defaults')).data,
  weeklyPreview: async () => (await api.get('/admin/cross-sell/weekly-report/preview')).data,
  weeklySend: async () => (await api.post('/admin/cross-sell/weekly-report/send')).data,
};

export const productsAPI = {
  search: async (q, limit = 8) => { const params = new URLSearchParams({ search: q }); const list = (await api.get(`/products?${params}`)).data; return (Array.isArray(list) ? list : list.items || []).slice(0, limit); },
  packEssence: async (id, vehicleParams = {}) => (await api.get(`/products/${id}/pack-essence`, { params: vehicleParams })).data,
  assistSpecs: async (data) => (await api.post('/admin/products/assist/specs', data)).data,
  parseRefsFile: async (file) => { const fd = new FormData(); fd.append('file', file); return (await api.post('/admin/products/parse-refs', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data; },
  fitCheck: async (productIds, vehicleParams) => (await api.post('/products/fit-check', { product_ids: productIds, ...vehicleParams })).data,
  compatibleAlternative: async (id, vehicleParams, limit = 5) => (await api.get(`/products/${id}/compatible-alternative`, { params: { limit, ...(vehicleParams || {}) } })).data,
  near: async (q) => (await api.get('/search/near', { params: { q } })).data,
  logSearch: (payload) => api.post('/search/log', payload).catch(() => {}),
  getAll: async (category = null, search = null, vehicle = null, manufacturer = null, equivBrand = null) => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (search) params.append('search', search);
    if (manufacturer) params.append('manufacturer', manufacturer);
    if (equivBrand) params.append('equiv_brand', equivBrand);
    if (vehicle) {
      Object.entries(vehicle).forEach(([k, v]) => v && params.append(k, v));
    }
    const response = await api.get(`/products?${params.toString()}`);
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },
  getByIds: async (ids) => (await api.get('/products', { params: { ids: ids.join(',') } })).data,
  getNew: async (days = 30, vehicle = null, limit = null) => (await api.get('/new-products', { params: { days, ...(vehicle || {}), ...(limit ? { limit } : {}) } })).data,
  subscribeNew: async (payload) => (await api.post('/new-products/subscribe', payload)).data,
  mySubscriptions: async () => (await api.get('/new-products/subscriptions')).data,
  deleteSubscription: async (id) => (await api.delete(`/new-products/subscriptions/${id}`)).data,
  adminSubsStats: async () => (await api.get('/admin/new-products/subscriptions/stats')).data,
  adminSubsSend: async () => (await api.post('/admin/new-products/subscriptions/send', {}, { timeout: 300000 })).data,
  adminSubsTest: async () => (await api.post('/admin/new-products/subscriptions/test')).data,
  newSummary: async () => (await api.get('/new-products/summary')).data,
  getEquivalents: async (id) => (await api.get(`/products/${id}/equivalents`)).data,
  notifyMe: async (id, email, lang, kind = 'stock') => (await api.post(`/products/${id}/notify-me`, { email: email || null, lang, kind })).data,
  notifyMeStatus: async (id) => (await api.get(`/products/${id}/notify-me`)).data,
  notifyMeRemove: async (id, kind = 'stock') => (await api.delete(`/products/${id}/notify-me`, { params: { kind } })).data,
  getEquivBrandLogos: async () => (await api.get('/equivalences/brand-logos')).data,
  trackEquiv: (brand, ref, productId, source = 'table') => api.post('/equivalences/track', { brand, ref, product_id: productId, source }).catch(() => {}),
  getFitments: async (id, flat = false) => (await api.get(`/products/${id}/fitments`, { params: flat ? { flat: 1 } : {} })).data,
  getRelatedLinks: async (id, lang) => (await api.get(`/products/${id}/related-links`, { params: { lang } })).data,
  getGuides: async (id, lang) => (await api.get(`/products/${id}/guides`, { params: { lang } })).data,
  categoryGuides: async (category, sub, lang) => (await api.get('/catalog/guides', { params: { category, sub: sub || undefined, lang } })).data,
  modelGuides: async (categories, subcategories, part, lang) => (await api.get('/catalog/guides-for-model', { params: { categories: categories.join(','), subcategories: subcategories.join('|'), part, lang } })).data,
  topModels: async (limit = 12) => (await api.get('/vehicles/top-models', { params: { limit } })).data,
  popularModels: async (category, sub, lang) => (await api.get('/catalog/popular-models', { params: { category, sub: sub || undefined, lang } })).data,
  compatSync: async (id) => (await api.post(`/admin/products/${id}/compat-sync`)).data,
  compatSyncStatus: async () => (await api.get('/admin/products/compat-sync')).data,
  compatSyncAll: async () => (await api.post('/admin/products/compat-sync')).data,
  compatDigest: async () => (await api.post('/admin/products/compat-digest')).data,
  weeklyDigestPreview: async () => (await api.get('/admin/digest/preview')).data,
  compatPending: async () => (await api.get('/admin/products/compat-pending')).data,
  compatPendingAction: async (id, action) => (await api.post(`/admin/products/${id}/compat-pending/${action}`)).data,
  compatConflicts: async (refresh = false) => (await api.get('/admin/products/compat-conflicts', { params: refresh ? { refresh: 1 } : {} })).data,
  compatConflictAction: async (id, otherId, action) => (await api.post(`/admin/products/${id}/compat-conflicts/${otherId}/${action}`)).data,
  getSimilar: async (id, limit = 8, vehicleParams = null) => (await api.get(`/products/${id}/similar`, { params: { limit, ...(vehicleParams || {}) } })).data,
  
  create: async (productData) => {
    const response = await api.post('/products', productData);
    return response.data;
  },
  
  update: async (id, productData) => {
    const response = await api.put(`/products/${id}`, productData);
    return response.data;
  },

  quickEdit: async (id, fields) => (await api.patch(`/admin/products/${id}/quick`, fields)).data,
  
  delete: async (id) => {
    const response = await api.delete(`/products/${id}`);
    return response.data;
  }
};

// Cart API (server cart when logged in, browser cart for guests)
const GUEST_CART_KEY = 'guestCart';
export const notifyCartChanged = () => window.dispatchEvent(new Event('cart-updated'));
const hasToken = () => !!localStorage.getItem('token');
const gaAdd = (p, quantity, pack) => import('./ga').then(({ ga }) => ga.addToCart(pack ? { ...p, price: pack.price, name: `${p.name} (${pack.label})` } : p, quantity)).catch(() => {});

export const guestCart = {
  read: () => { try { return JSON.parse(localStorage.getItem(GUEST_CART_KEY)) || []; } catch { return []; } },
  write: (items) => { localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items)); notifyCartChanged(); },
  clear: () => { localStorage.removeItem(GUEST_CART_KEY); notifyCartChanged(); },
};

const lineKey = (i) => `${i.product_id}::${i.pack_id || ''}`;
const applyGuestTier = (line, p) => {
  if (line.pack_id) return line;
  const t = (p.tiers || []).filter(x => x.min_qty <= line.quantity).sort((a, b) => b.discount_pct - a.discount_pct)[0];
  if (t) { line.base_price = p.price; line.discount_pct = t.discount_pct; line.price = Math.round(p.price * (1 - t.discount_pct / 100) * 100) / 100; }
  else { line.base_price = null; line.discount_pct = null; line.price = p.price; }
  return line;
};
const cartUnits = (items, productId) => items.filter(i => i.product_id === productId).reduce((n, i) => n + i.quantity * (i.units || 1), 0);
const shortMsg = (available, units) => available <= 0
  ? "Rupture de stock : cet article n'est plus disponible"
  : units > 1 ? `Stock insuffisant : ${available} unité(s) disponible(s), soit ${Math.floor(available / units)} conditionnement(s)` : `Stock insuffisant : ${available} exemplaire(s) disponible(s)`;

export const cartAPI = {
  packUpsell: async (productIds, vehicleParams = {}) => (await api.post('/cart/pack-upsell', { product_ids: productIds, ...vehicleParams })).data,
  get: async () => {
    if (!hasToken()) return { items: guestCart.read(), guest: true };
    const response = await api.get('/cart');
    return response.data;
  },
  
  addItem: async (productId, quantity = 1, packId = null) => {
    try {
      if (!hasToken()) {
        const items = guestCart.read();
        const p = (await api.get(`/products/${productId}`)).data;
        const pack = packId ? (p.packs || []).find(x => x.id === packId) : null;
        if (packId && !pack) throw stockError('Conditionnement introuvable');
        const units = pack ? pack.units : 1;
        const available = p.available_stock ?? p.stock ?? 50;
        const already = cartUnits(items, productId);
        if (available <= 0) throw stockError(shortMsg(0, units));
        if (already + quantity * units > available) throw stockError(shortMsg(Math.max(available - already, 0), units));
        const mult = p.sale_multiple || 1;
        let existing = items.find(i => i.product_id === productId && (i.pack_id || null) === (packId || null));
        const nextQty = Math.min((existing ? existing.quantity : 0) + quantity, 99);
        if (!pack && mult > 1 && nextQty % mult !== 0) throw stockError(`Cet article est vendu par ${mult}${p.sale_multiple_label ? ` (${p.sale_multiple_label})` : ''} : choisissez une quantité multiple de ${mult}`);
        if (existing) existing.quantity = nextQty;
        else { existing = { product_id: p.id, name: p.name, brand: p.brand, ref: p.ref, price: pack ? pack.price : p.price, image: p.image, quantity, pack_id: pack ? pack.id : null, pack_label: pack ? pack.label : null, units, sale_multiple: mult }; items.push(existing); }
        applyGuestTier(existing, p);
        guestCart.write(items);
        gaAdd(p, quantity, pack);
        openCartDrawer(productId);
        return { message: 'Produit ajouté au panier', cart_count: items.length };
      }
      const response = await api.post('/cart/add', { product_id: productId, quantity, pack_id: packId || null });
      notifyCartChanged();
      openCartDrawer(productId);
      api.get(`/products/${productId}`).then((r) => gaAdd(r.data, quantity, packId ? (r.data.packs || []).find((x) => x.id === packId) : null)).catch(() => {});
      return response.data;
    } catch (error) {
      if (error.response?.status === 409 || error.isStockError) {
        const { toast } = await import('sonner');
        toast.error(error.response?.data?.detail || error.message);
      }
      throw error;
    }
  },
  
  updateItem: async (productId, quantity, packId = null) => {
    if (!hasToken() && quantity > 0) {
      const items = guestCart.read();
      const line = items.find(i => i.product_id === productId && (i.pack_id || null) === (packId || null));
      const p = (await api.get(`/products/${productId}`)).data;
      const available = p.available_stock ?? p.stock ?? 50;
      const units = line?.units || 1;
      const others = cartUnits(items.filter(i => lineKey(i) !== lineKey({ product_id: productId, pack_id: packId })), productId);
      if (others + quantity * units > available) {
        const { toast } = await import('sonner');
        toast.error(shortMsg(Math.max(available - others, 0), units));
        throw stockError('Stock insuffisant');
      }
    }
    if (!hasToken()) {
      const p = quantity > 0 && !packId ? (await api.get(`/products/${productId}`)).data : null;
      if (p && (p.sale_multiple || 1) > 1 && quantity % p.sale_multiple !== 0) {
        const { toast } = await import('sonner');
        toast.error(`Cet article est vendu par ${p.sale_multiple} : choisissez une quantité multiple de ${p.sale_multiple}`);
        throw stockError('Multiple de vente');
      }
      guestCart.write(guestCart.read().map(i => (i.product_id === productId && (i.pack_id || null) === (packId || null)) ? (p ? applyGuestTier({ ...i, quantity }, p) : { ...i, quantity }) : i));
      return { message: 'Panier mis à jour' };
    }
    try {
      const response = await api.put(`/cart/update/${productId}?quantity=${quantity}${packId ? `&pack_id=${encodeURIComponent(packId)}` : ''}`);
      notifyCartChanged();
      return response.data;
    } catch (error) {
      if (error.response?.status === 409) {
        const { toast } = await import('sonner');
        toast.error(error.response.data?.detail || 'Stock insuffisant');
      }
      throw error;
    }
  },
  
  removeItem: async (productId, packId = null) => {
    if (!hasToken()) {
      guestCart.write(guestCart.read().filter(i => !(i.product_id === productId && (i.pack_id || null) === (packId || null))));
      return { message: 'Produit retiré' };
    }
    const response = await api.delete(`/cart/remove/${productId}${packId ? `?pack_id=${encodeURIComponent(packId)}` : ''}`);
    notifyCartChanged();
    return response.data;
  },

  mergeGuestCart: async () => {
    const items = guestCart.read();
    if (!items.length || !hasToken()) return null;
    const response = await api.post('/cart/merge', { items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity, pack_id: i.pack_id || null })) });
    guestCart.clear();
    return response.data;
  }
};

export const cartLineKey = lineKey;

// Orders API
export const fittingAPI = {
  centres: async () => (await api.get('/fitting/centres')).data,
  quote: async (postcode, city) => (await api.get('/fitting/quote', { params: { postcode: postcode || undefined, city: city || undefined } })).data,
  adminList: async () => (await api.get('/admin/fitting/centres')).data,
  create: async (data) => (await api.post('/admin/fitting/centres', data)).data,
  update: async (id, data) => (await api.put(`/admin/fitting/centres/${id}`, data)).data,
  remove: async (id) => (await api.delete(`/admin/fitting/centres/${id}`)).data,
  appointment: async (token) => (await api.get(`/fitting/appointment/${token}`)).data,
  confirmAppointment: async (token, data) => (await api.post(`/fitting/appointment/${token}`, data)).data,
  appointments: async () => (await api.get('/admin/fitting/appointments')).data,
  remindCentre: async (orderId) => (await api.post(`/admin/fitting/appointments/${orderId}/remind-centre`)).data,
  getSettings: async () => (await api.get('/admin/fitting/settings')).data,
  setSettings: async (data) => (await api.put('/admin/fitting/settings', data)).data,
  getPricing: async () => (await api.get('/admin/tyres/pricing')).data,
  setPricing: async (data) => (await api.put('/admin/tyres/pricing', data)).data,
  applyPricing: async () => (await api.post('/admin/tyres/pricing/apply')).data,
  slots: async (centreId) => (await api.get(`/fitting/centres/${centreId}/slots`)).data,
  centreReviews: async (centreId) => (await api.get(`/fitting/centres/${centreId}/reviews`)).data,
  selfService: async (token) => (await api.get(`/fitting/centre/${token}`)).data,
  selfServiceSchedule: async (token, data) => (await api.put(`/fitting/centre/${token}/schedule`, data)).data,
  selfServiceLang: async (token, lang) => (await api.put(`/fitting/centre/${token}/lang`, { lang })).data,
  setSchedule: async (id, data) => (await api.put(`/admin/fitting/centres/${id}/schedule`, data)).data,
  sendScheduleLink: async (id) => (await api.post(`/admin/fitting/centres/${id}/schedule-link`)).data,
  review: async (token) => (await api.get(`/fitting/review/${token}`)).data,
  postReview: async (token, data) => (await api.post(`/fitting/review/${token}`, data)).data,
  adminReviews: async () => (await api.get('/admin/fitting/reviews')).data,
  deleteReview: async (id) => (await api.delete(`/admin/fitting/reviews/${id}`)).data,
  runReviewRequests: async () => (await api.post('/admin/fitting/reviews/run')).data,
  centreReply: async (token, rid, message) => (await api.put(`/fitting/centre/${token}/reviews/${rid}/reply`, { message })).data,
  centreDeleteReply: async (token, rid) => (await api.delete(`/fitting/centre/${token}/reviews/${rid}/reply`)).data,
  adminDeleteReply: async (rid) => (await api.delete(`/admin/fitting/reviews/${rid}/reply`)).data,
  policy: async () => (await api.get('/fitting/policy')).data,
  reschedule: async (orderId, date) => (await api.post(`/orders/${orderId}/fitting/reschedule`, { date })).data,
  cancelAppointment: async (orderId) => (await api.post(`/orders/${orderId}/fitting/cancel-appointment`)).data,
  cancelService: async (orderId) => (await api.post(`/orders/${orderId}/fitting/cancel-service`)).data,
  creditNote: (orderId, number, lang = 'fr') => downloadBlob(`/orders/${orderId}/fitting/credit-note?lang=${lang}`, `${lang === 'en' ? 'credit-note' : 'avoir'}-${number}.pdf`),
  statements: async (month) => (await api.get('/admin/fitting/statements', { params: { month: month || undefined } })).data,
  statementPdf: (cid, month) => downloadBlob(`/admin/fitting/centres/${cid}/statement.pdf?month=${month}`, `releve-montages-${month}.pdf`),
  sendStatement: async (cid, month) => (await api.post(`/admin/fitting/centres/${cid}/statement/send?month=${month}`)).data,
  selfServiceStatements: async (token) => (await api.get(`/fitting/centre/${token}/statements`)).data,
  validateStatement: async (token, month) => (await api.post(`/fitting/centre/${token}/statements/${month}/validate`)).data,
  disputeStatement: async (token, month, reason) => (await api.post(`/fitting/centre/${token}/statements/${month}/dispute`, { reason })).data,
  resolveStatement: async (cid, month, note) => (await api.post(`/admin/fitting/centres/${cid}/statement/resolve?month=${month}`, { note })).data,
  cardRefund: async (orderId, reference) => (await api.post(`/admin/orders/${orderId}/fitting/card-refund`, { reference })).data,
  uploadCentreInvoice: async (token, month, file, invoiceNumber, amount) => { const fd = new FormData(); fd.append('file', file); fd.append('invoice_number', invoiceNumber); fd.append('amount', amount); return (await api.post(`/fitting/centre/${token}/statements/${month}/invoice`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data; },
  markStatementPaid: async (cid, month, reference) => (await api.post(`/admin/fitting/centres/${cid}/statement/mark-paid?month=${month}`, { reference })).data,
  remindStatements: async (cid, month) => (await api.post(`/admin/fitting/statements/remind${cid ? `?cid=${cid}&month=${month}` : ''}`)).data,
  rejectInvoice: async (cid, month, reason) => (await api.post(`/admin/fitting/centres/${cid}/statement/reject-invoice?month=${month}`, { reason })).data,
  dueAlert: async () => (await api.post('/admin/fitting/invoices/due-alert')).data,
  stats: async () => (await api.get('/admin/fitting/stats')).data,
  statsCsv: () => downloadBlob('/admin/fitting/stats.csv', 'centres-montage.csv'),
  unconfirmed: async () => (await api.get('/admin/fitting/unconfirmed')).data,
  sendUnconfirmedAlert: async (force = false) => (await api.post(`/admin/fitting/unconfirmed/alert?force=${force}`)).data,
  reassign: async (orderId, data) => (await api.post(`/admin/fitting/appointments/${orderId}/reassign`, data)).data,
  getAutoReassign: async () => (await api.get('/admin/fitting/auto-reassign')).data,
  setAutoReassign: async (enabled) => (await api.put('/admin/fitting/auto-reassign', { auto_reassign_enabled: enabled })).data,
  runAutoReassign: async () => (await api.post('/admin/fitting/auto-reassign/run')).data,
  visibilityCheck: async () => (await api.post('/admin/fitting/visibility/check')).data,
  reliabilityHistory: async (id, weeks = 12) => (await api.get(`/admin/fitting/centres/${id}/reliability-history?weeks=${weeks}`)).data,
};

export const shippingAPI = {
  options: async (country, subtotal, tyreQty = 0, weightKg = 0) => (await api.get('/shipping/options', { params: { country, subtotal, tyre_qty: tyreQty, weight_kg: weightKg } })).data,
  summary: async (country = 'FR') => (await api.get('/shipping/summary', { params: { country } })).data,
  countries: async () => (await api.get('/shipping/countries')).data,
  adminGet: async () => (await api.get('/admin/shipping-settings')).data,
  adminSave: async (data) => (await api.put('/admin/shipping-settings', data)).data,
};

export const paymentsAPI = {
  sumupCheckout: async (orderId) => (await api.post(`/payments/sumup/checkout/${orderId}`)).data,
  sumupConfirm: async (orderId) => (await api.post(`/payments/sumup/confirm/${orderId}`)).data,
  payLink: async (token) => (await api.post(`/pay/${token}`)).data,
};

export const ordersAPI = {
  getAll: async () => {
    const response = await api.get('/orders');
    return response.data;
  },
  
  create: async (orderData) => {
    const attribution = getShareAttribution();
    const response = await api.post('/orders', { lang: i18n.get(), ...(attribution ? { attribution } : {}), ...orderData });
    notifyCartChanged();
    return response.data;
  },
  
  updateStatus: async (orderId, status) => {
    const response = await api.put(`/orders/${orderId}/status?status=${status}`);
    return response.data;
  },

  cancel: async (orderId) => {
    const response = await api.post(`/orders/${orderId}/cancel`);
    return response.data;
  },

  getById: async (orderId) => {
    const response = await api.get(`/orders/${orderId}`);
    return response.data;
  },

  sendReviewReminder: async (orderId) => (await api.post(`/admin/orders/${orderId}/review-reminder`)).data,
  resendPaymentLink: async (orderId) => (await api.post(`/admin/orders/${orderId}/payment-link/resend`)).data,
  customerPaymentLink: async (orderId) => (await api.post(`/orders/${orderId}/payment-link`)).data,
  addTrackingEvent: async (orderId, data) => (await api.post(`/orders/${orderId}/tracking-events`, data)).data,
  deleteTrackingEvent: async (orderId, eventId) => (await api.delete(`/orders/${orderId}/tracking-events/${eventId}`)).data,

  updateTracking: async (orderId, carrier, trackingNumber) => {
    const response = await api.put(`/orders/${orderId}/tracking`, { carrier, tracking_number: trackingNumber });
    return response.data;
  },

  clearTracking: async (orderId) => {
    const response = await api.delete(`/orders/${orderId}/tracking`);
    return response.data;
  },

  getCarriers: async () => {
    const response = await api.get('/carriers');
    return response.data;
  },

  reorder: async (orderId) => { const r = (await api.post(`/orders/${orderId}/reorder`)).data; notifyCartChanged(); return r; },
  downloadCreditNote: async (order, lang = 'fr') => {
    const number = order.refund?.credit_note_number || order.order_number;
    const response = await api.get(`/orders/${order.id}/credit-note`, { responseType: 'blob', params: { lang } });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${lang === 'en' ? 'credit-note' : 'avoir'}-${number}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  downloadInvoice: async (order, lang = 'fr') => {
    const response = await api.get(`/orders/${order.id}/invoice`, { responseType: 'blob', params: { lang } });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${lang === 'en' ? 'invoice' : 'facture'}-${(order.order_number || order.id.slice(0, 8)).replace(/^AD-/, 'FA-')}${lang === 'en' ? '-en' : ''}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
};

export const vehicleAlertsAPI = {
  mine: async () => (await api.get('/account/vehicle-alerts')).data,
  setFrequency: async (id, frequency) => (await api.patch(`/account/vehicle-alerts/${id}`, { frequency })).data,
  remove: async (id) => (await api.delete(`/account/vehicle-alerts/${id}`)).data,
  adminStats: async () => (await api.get('/admin/vehicle-alerts/stats')).data,
  adminWeeklyPreview: async () => (await api.get('/admin/vehicle-alerts/weekly-recap')).data,
  adminWeeklySend: async () => (await api.post('/admin/vehicle-alerts/weekly-recap/send')).data,
  adminWeeklyPreviewHtml: async (alertId) => (await api.get('/admin/vehicle-alerts/weekly-recap/preview-html', { params: { alert_id: alertId || undefined } })).data,
  adminWeeklyTest: async (alertId) => (await api.post('/admin/vehicle-alerts/weekly-recap/test', { alert_id: alertId || null })).data,
  adminExportCsv: () => downloadBlob('/admin/vehicle-alerts/export.csv', `alertes-vehicules-${new Date().toISOString().slice(0, 10)}.csv`),
  suggestions: async () => (await api.get('/account/vehicle-alerts/suggestions')).data,
  fromGarage: async (vehicleIds, frequency = 'weekly') => (await api.post('/account/vehicle-alerts/from-garage', { vehicle_ids: vehicleIds || null, frequency })).data,
};

export const accountAlertsAPI = {
  list: async () => (await api.get('/account/alerts')).data,
  remove: async (id) => (await api.delete(`/account/alerts/${id}`)).data,
  removeAll: async () => (await api.delete('/account/alerts')).data,
  wishlistPrice: async () => (await api.post('/account/alerts/wishlist-price')).data,
  badge: async () => (await api.get('/account/alerts/badge')).data,
  seen: async () => (await api.post('/account/alerts/seen')).data,
};

export const myVehiclesAPI = {
  list: async () => (await api.get('/account/vehicles')).data,
  add: async (data) => (await api.post('/account/vehicles', data)).data,
  remove: async (id) => (await api.delete(`/account/vehicles/${id}`)).data,
  sync: async (vehicle) => (await api.post('/account/vehicles/sync', { brand: vehicle.brand, model: vehicle.model, year: vehicle.year, engine: vehicle.engine || null, plate: vehicle.plate || null })).data,
  last: async () => (await api.get('/account/vehicles/last')).data,
};

export const bannersAPI = {
  getActive: async () => (await api.get('/banners')).data,
  getAll: async () => (await api.get('/admin/banners')).data,
  create: async (data) => (await api.post('/admin/banners', data)).data,
  update: async (id, data) => (await api.put(`/admin/banners/${id}`, data)).data,
  delete: async (id) => (await api.delete(`/admin/banners/${id}`)).data,
  reorder: async (ids) => (await api.put('/admin/banners/reorder', ids)).data,
};

export const promosAPI = {
  validate: async (code) => (await api.post('/promos/validate', { code })).data,
  banner: async () => (await api.get('/promos/banner')).data,
  adminList: async () => (await api.get('/admin/promos')).data,
  create: async (data) => (await api.post('/admin/promos', data)).data,
  update: async (id, data) => (await api.put(`/admin/promos/${id}`, data)).data,
  remove: async (id) => (await api.delete(`/admin/promos/${id}`)).data,
};

export const settingsAPI = {
  getCompany: async () => {
    const response = await api.get('/admin/settings/company');
    return response.data;
  },
  googleBusiness: async () => (await api.get('/admin/google-business')).data,
  faviconSync: async () => (await api.post('/admin/branding/favicon-sync')).data,
  saveGoogleBusiness: async (data) => (await api.put('/admin/google-business', data)).data,
  refreshGoogleBusiness: async () => (await api.post('/admin/google-business/refresh')).data,
  feedStatus: async (refresh = false) => (await api.get('/admin/feeds/status', { params: refresh ? { refresh: true } : {} })).data,
  updateCompany: async (data) => {
    const response = await api.put('/admin/settings/company', data);
    return response.data;
  }
};

export const cartRemindersAPI = {
  overview: async () => (await api.get('/admin/cart-reminders')).data,
  runNow: async () => (await api.post('/admin/cart-reminders/run')).data,
  send: async (userId) => (await api.post(`/admin/cart-reminders/send/${userId}`)).data,
};

// Categories API
export const uploadsAPI = {
  uploadImage: async (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/admin/uploads', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => onProgress && e.total && onProgress(Math.round((e.loaded * 100) / e.total)),
    });
    return response.data;
  },
  resolveUrl: (url) => (url && url.startsWith('/api/') ? `${BACKEND_URL}${url}` : url),
};

export const categoriesAPI = {
  getAll: async () => {
    const response = await api.get('/categories');
    return response.data;
  },
  setSpecFields: async (id, subcategory, fields) => (await api.put(`/admin/categories/${id}/spec-fields`, { subcategory, fields })).data,
  groupSubcategories: async (id, parent, children) => (await api.post(`/admin/categories/${id}/group-subcategories`, { parent, children })).data,
  create: async (data) => {
    const response = await api.post('/admin/categories', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/admin/categories/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/admin/categories/${id}`);
    return response.data;
  },
  generateSeo: async (id) => (await api.post(`/admin/categories/${id}/generate-seo`)).data,
  generateAll: async () => (await api.post('/admin/categories/generate-all')).data,
  generateSubSeo: async (id, subcategories, overwrite = false) => (await api.post(`/admin/categories/${id}/generate-subcategory-seo`, { subcategories, overwrite }, { timeout: 120000 })).data,
  fillSubSeo: async (id, subcategories = null, overwrite = false) => (await api.post(`/admin/categories/${id}/fill-subcategory-seo`, { subcategories, overwrite })).data,
  fillAllSubSeo: async (overwrite = false) => (await api.post('/admin/categories/fill-subcategory-seo', { overwrite })).data,
  rewriteSubSeoBulk: async (limit = null) => (await api.post('/admin/categories/rewrite-subseo-bulk', { limit })).data,
  rewriteSubSeoStatus: async () => (await api.get('/admin/categories/rewrite-subseo-status')).data,
  subSeoStatus: async () => (await api.get('/admin/categories/subcategory-seo/status')).data,
  subImagesStatus: async (categoryId = null) => (await api.get('/admin/categories/images/ai/status', { params: categoryId ? { category_id: categoryId } : {} })).data,
  generateSubImages: async (body) => (await api.post('/admin/categories/images/ai/generate', body)).data,
  suggestSubSeo: async (id, subcategory) => (await api.post(`/admin/categories/${id}/subcategory-seo/suggest`, { subcategory }, { timeout: 120000 })).data,
  applySubSeo: async (id, subcategory, seo_title, meta_description) => (await api.post(`/admin/categories/${id}/subcategory-seo/apply`, { subcategory, seo_title, meta_description })).data,
};

export const i18nAPI = {
  translate: async (texts, context = '') => (await api.post('/admin/i18n/translate', { texts, context }, { timeout: 120000 })).data,
  translateCatalog: async (overwrite = false) => (await api.post('/admin/i18n/translate-catalog', { overwrite })).data,
  translateSeo: async (categoryId = null, overwrite = false) => (await api.post('/admin/i18n/translate-seo', { category_id: categoryId, overwrite })).data,
  translateArticles: async (articleId = null, overwrite = false) => (await api.post('/admin/i18n/translate-articles', { article_id: articleId, overwrite })).data,
  translatePages: async (pageId = null, overwrite = false) => (await api.post('/admin/i18n/translate-pages', { page_id: pageId, overwrite })).data,
  translateProducts: async (productId = null, overwrite = false) => (await api.post('/admin/i18n/translate-products', { product_id: productId, overwrite })).data,
  status: async () => (await api.get('/admin/i18n/status')).data,
};

export const brandsAPI = {
  logoFetchStatus: async () => (await api.get('/admin/brand-logos/status')).data,
  logoFetchStart: async (names = null, force = false) => (await api.post('/admin/brand-logos/fetch', { names, force })).data,
  logoForget: async (name) => (await api.delete(`/admin/brand-logos/${encodeURIComponent(name)}`)).data,
  getAll: async () => {
    const response = await api.get('/brands');
    return response.data;
  },
  getOne: async (name) => {
    const response = await api.get(`/brands/${encodeURIComponent(name)}`);
    return response.data;
  },
  setLogo: async (name, logo, description) => {
    const response = await api.put(`/admin/brands/${encodeURIComponent(name)}`, { logo, description });
    return response.data;
  },
  remove: async (name) => (await api.delete(`/admin/brands/${encodeURIComponent(name)}`)).data,
  save: async (name, data) => (await api.put(`/admin/brands/${encodeURIComponent(name)}`, data)).data,
  generateSeo: async (name, mainProduct) => (await api.post(`/admin/brands/${encodeURIComponent(name)}/generate-seo`, null, { params: mainProduct ? { main_product: mainProduct } : {} })).data,
  seoCheck: async (name) => (await api.get(`/admin/brands/${encodeURIComponent(name)}/seo-check`)).data,
  fillSeo: async (name) => (await api.post(`/admin/brands/${encodeURIComponent(name)}/fill-seo`)).data,
  fillAllSeo: async (overwrite = false) => (await api.post('/admin/brands/fill-seo', { overwrite })).data,
  seoStatus: async () => (await api.get('/admin/brands/seo-status')).data,
  rewriteSeoBulk: async (onlyWeak = true, names = null) => (await api.post('/admin/brands/rewrite-seo-bulk', { only_weak: onlyWeak, names })).data,
  rewriteSeoStatus: async () => (await api.get('/admin/brands/rewrite-seo-status')).data,
  rewriteSeo: async (name) => (await api.post(`/admin/brands/${encodeURIComponent(name)}/rewrite-seo`, null, { timeout: 300000 })).data,
  generateAll: async () => (await api.post('/admin/brands/generate-all')).data,
};

export const pagesAPI = {
  list: async () => (await api.get('/pages')).data,
  get: async (slug) => (await api.get(`/pages/${encodeURIComponent(slug)}`)).data,
  adminList: async () => (await api.get('/admin/pages')).data,
  create: async (data) => (await api.post('/admin/pages', data)).data,
  update: async (id, data) => (await api.put(`/admin/pages/${id}`, data)).data,
  delete: async (id) => (await api.delete(`/admin/pages/${id}`)).data,
  reset: async (id) => (await api.post(`/admin/pages/${id}/reset`)).data,
};

// Reviews API
export const reviewsAPI = {
  reward: async () => (await api.get('/reviews/reward')).data,
  wall: async () => (await api.get('/reviews/wall')).data,
  googleBusiness: async () => (await api.get('/google-business')).data,
  translate: async (reviewId, lang) => (await api.post(`/reviews/${reviewId}/translate`, null, { params: { lang } })).data,
  getForProduct: async (productId) => {
    const response = await api.get(`/products/${productId}/reviews`, { params: { voter: getVoterId() } });
    return response.data;
  },
  create: async (productId, reviewData) => {
    const response = await api.post(`/products/${productId}/reviews`, reviewData);
    return response.data;
  },
  uploadPhoto: async (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/uploads/review-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => onProgress && e.total && onProgress(Math.round((e.loaded * 100) / e.total)),
    });
    return response.data;
  },
  mine: async () => {
    const response = await api.get('/reviews/mine');
    return response.data.product_ids;
  },
  toggleHelpful: async (reviewId) => (await api.post(`/reviews/${reviewId}/helpful`, { voter_id: getVoterId() })).data,
  reportReasons: async () => (await api.get('/reviews/report-reasons')).data,
  report: async (reviewId, reason, comment) => (await api.post(`/reviews/${reviewId}/report`, { reason, comment, voter_id: getVoterId() })).data,
};

export const questionsAPI = {
  forProduct: async (productId) => (await api.get(`/products/${productId}/questions`, { params: { voter: getVoterId() } })).data,
  ask: async (productId, data) => (await api.post(`/products/${productId}/questions`, { ...data, voter_id: getVoterId() })).data,
  toggleHelpful: async (questionId) => (await api.post(`/questions/${questionId}/helpful`, { voter_id: getVoterId() })).data,
  adminList: async (status) => (await api.get('/admin/questions', { params: status ? { status } : {} })).data,
  answer: async (questionId, message) => (await api.put(`/admin/questions/${questionId}/answer`, { message })).data,
  remove: async (questionId) => (await api.delete(`/admin/questions/${questionId}`)).data,
};

const getVoterId = () => {
  let id = localStorage.getItem('reviewVoterId');
  if (!id) {
    id = Array.from(crypto.getRandomValues(new Uint8Array(12)), b => b.toString(16).padStart(2, '0')).join('');
    localStorage.setItem('reviewVoterId', id);
  }
  return id;
};

// Admin API
export const pricingAPI = {
  suggestions: async () => (await api.get('/admin/products/suggested-prices')).data,
  apply: async (productId) => (await api.post(`/admin/products/${productId}/apply-suggested-price`)).data,
  applyAll: async () => (await api.post('/admin/products/apply-suggested-prices')).data,
};

const downloadBlob = async (url, filename) => {
  const response = await api.get(url, { responseType: 'blob' });
  const href = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
};

export const exportAPI = {
  customers: (fmt) => downloadBlob(`/admin/customers/export.${fmt}`, `clients-${new Date().toISOString().slice(0, 10)}.${fmt}`),
  summary: async (from, to) => (await api.get(`/admin/export/summary?date_from=${from || ''}&date_to=${to || ''}`)).data,
  xlsx: (from, to) => downloadBlob(`/admin/export.xlsx?date_from=${from || ''}&date_to=${to || ''}`, `export-comptable-${from || 'debut'}_${to || 'aujourdhui'}.xlsx`),
  csv: (sheet, from, to) => downloadBlob(`/admin/export.csv?sheet=${sheet}&date_from=${from || ''}&date_to=${to || ''}`, `${sheet}-${from || 'debut'}_${to || 'aujourdhui'}.csv`),
  history: async () => (await api.get('/admin/export/history')).data,
  sendNow: async (month) => (await api.post(`/admin/export/send-now${month ? `?month=${month}` : ''}`)).data,
  accountantLinks: async () => (await api.get('/admin/accountant/links')).data,
  createAccountantLink: async (body) => (await api.post('/admin/accountant/links', body)).data,
  revokeAccountantLink: async (id) => (await api.post(`/admin/accountant/links/${id}/revoke`)).data,
};

export const accountantPortalAPI = {
  overview: async (token, month) => (await api.get(`/public/accountant/${token}/overview`, { params: { month: month || undefined } })).data,
  invoices: async (token, month) => (await api.get(`/public/accountant/${token}/invoices`, { params: { month: month || undefined } })).data,
  path: (token, suffix) => `${API}/public/accountant/${token}/${suffix}`,
  abs: (path) => `${BACKEND_URL}${path}`,
};

export const tyresAPI = {
  options: async () => (await api.get('/tyres/options')).data,
  searchTyres: async (params) => (await api.get('/tyres/search', { params })).data,
  searchWheels: async (params) => (await api.get('/tyres/wheels/search', { params })).data,
  detect: async (text, kind) => (await api.post('/tyres/detect', { text, kind })).data,
  vehicle: async (params) => (await api.get('/tyres/vehicle', { params })).data,
  vehicleSearch: async (params) => (await api.get('/tyres/vehicle/search', { params })).data,
  winterBanner: async (params) => (await api.get('/tyres/winter-banner', { params })).data,
};

export const adminTyreFitmentsAPI = {
  list: async (q = '') => (await api.get('/admin/tyre-fitments', { params: q ? { q } : {} })).data,
  create: async (data) => (await api.post('/admin/tyre-fitments', data)).data,
  update: async (id, data) => (await api.put(`/admin/tyre-fitments/${id}`, data)).data,
  remove: async (id) => (await api.delete(`/admin/tyre-fitments/${id}`)).data,
  template: () => downloadBlob('/admin/tyre-fitments/template.csv', 'modele-pneus-par-vehicule.csv'),
  importCsv: async (file, dryRun) => { const fd = new FormData(); fd.append('file', file); return (await api.post(`/admin/tyre-fitments/import?dry_run=${dryRun}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data; },
  seedDemo: async () => (await api.post('/admin/tyre-fitments/seed-demo')).data,
  seedWinterStock: async () => (await api.post('/admin/tyre-fitments/seed-winter-stock')).data,
  brandImages: async () => (await api.get('/admin/tyre-fitments/brand-images')).data,
  setBrandImages: async (images) => (await api.post('/admin/tyre-fitments/brand-images', { images })).data,
  deleteBrandImage: async (brand) => (await api.delete(`/admin/tyre-fitments/brand-images/${encodeURIComponent(brand)}`)).data,
  aiStatus: async () => (await api.get('/admin/tyre-fitments/ai/status')).data,
  aiGenerate: async (brand = null, limit = 500) => (await api.post('/admin/tyre-fitments/ai/generate', { brand, limit })).data,
  aiCancel: async () => (await api.post('/admin/tyre-fitments/ai/cancel')).data,
  aiSettings: async (onDemand) => (await api.put('/admin/tyre-fitments/ai/settings', { on_demand: onDemand })).data,
  aiGenerateOne: async (brand, model) => (await api.post('/admin/tyre-fitments/ai/generate-one', { brand, model })).data,
  verify: async (id) => (await api.put(`/admin/tyre-fitments/${id}/verify`)).data,
  tyresTemplate: () => downloadBlob('/admin/tyres/import/template.csv', 'modele-import-pneus.csv'),
  wheelsTemplate: () => downloadBlob('/admin/wheels/import/template.csv', 'modele-import-jantes.csv'),
  importWheels: async (file, dryRun) => { const fd = new FormData(); fd.append('file', file); return (await api.post(`/admin/wheels/import?dry_run=${dryRun}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data; },
  importTyres: async (file, dryRun) => { const fd = new FormData(); fd.append('file', file); return (await api.post(`/admin/tyres/import?dry_run=${dryRun}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data; },
};

export const wooImportAPI = {
  preview: async (file) => { const fd = new FormData(); fd.append('file', file); return (await api.post('/admin/catalog/import/woo/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data; },
  apply: async (jobId, downloadImages = true, updateExisting = true) => (await api.post('/admin/catalog/import/woo/apply', { job_id: jobId, download_images: downloadImages, update_existing: updateExisting })).data,
  job: async (jobId) => (await api.get(`/admin/catalog/import/woo/jobs/${jobId}`)).data,
};

export const catalogAPI = {
  template: () => downloadBlob('/admin/catalog-template.csv', 'modele-catalogue-produits.csv'),
  export: () => downloadBlob('/admin/catalog-export.csv', 'catalogue-produits.csv'),
  preview: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return (await api.post('/admin/catalog/import/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },
  apply: async (creates, updates) => (await api.post('/admin/catalog/import/apply', { creates, updates })).data,
};

export const reportsAPI = {
  monthly: async (month) => (await api.get(`/admin/reports/monthly${month ? `?month=${month}` : ''}`)).data,
};

export const sellersAPI = {
  activityTypes: async () => (await api.get('/sellers/activity-types')).data,
  me: async () => (await api.get('/sellers/me')).data,
  access: async () => (await api.get('/sellers/me/access')).data,
  create: async (data) => (await api.post('/sellers/me', data)).data,
  update: async (data) => (await api.put('/sellers/me', data)).data,
  submit: async () => (await api.post('/sellers/me/submit')).data,
  uploadDocument: async (kind, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return (await api.post('/sellers/me/documents', fd, { params: { kind }, headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },
  deleteDocument: async (docId) => (await api.delete(`/sellers/me/documents/${docId}`)).data,
  openDocument: async (docId) => (await api.get(`/sellers/me/documents/${docId}`, { responseType: 'blob' })).data,
  adminList: async (params) => (await api.get('/admin/sellers', { params })).data,
  adminGet: async (id) => (await api.get(`/admin/sellers/${id}`)).data,
  adminDocument: async (id, docId) => (await api.get(`/admin/sellers/${id}/documents/${docId}`, { responseType: 'blob' })).data,
  adminAction: async (id, action, body = {}) => (await api.post(`/admin/sellers/${id}/${action}`, body)).data,
};

export const listingsAPI = {
  mine: async (status) => (await api.get('/seller/listings', { params: status ? { status } : {} })).data,
  get: async (id) => (await api.get(`/seller/listings/${id}`)).data,
  create: async (data) => (await api.post('/seller/listings', data)).data,
  update: async (id, data) => (await api.put(`/seller/listings/${id}`, data)).data,
  stock: async (id, quantity) => (await api.put(`/seller/listings/${id}/stock`, null, { params: { quantity } })).data,
  action: async (id, action) => (await api.post(`/seller/listings/${id}/${action}`)).data,
  uploadPhoto: async (id, file) => { const fd = new FormData(); fd.append('file', file); return (await api.post(`/seller/listings/${id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data; },
  photoSrc: (url) => `${BACKEND_URL}${url}${url.includes('?') ? '&' : '?'}t=${encodeURIComponent(localStorage.getItem('token') || '')}`,
  deletePhoto: async (id, pid) => (await api.delete(`/seller/listings/${id}/photos/${pid}`)).data,
  setMain: async (id, pid) => (await api.put(`/seller/listings/${id}/photos/${pid}/main`)).data,
  adminList: async (params) => (await api.get('/admin/listings', { params })).data,
  adminGet: async (id) => (await api.get(`/admin/listings/${id}`)).data,
  adminAction: async (id, action, reason = '') => (await api.post(`/admin/listings/${id}/${action}`, { reason })).data,
  publicList: async (params) => (await api.get('/occasion/listings', { params })).data,
  publicGet: async (ref) => (await api.get(`/occasion/listings/${ref}`)).data,
};

export const importsAPI = {
  fields: async () => (await api.get('/seller/imports/fields')).data,
  templateUrl: (fmt) => `${BACKEND_URL}/api/seller/imports/templates/${fmt}`,
  list: async () => (await api.get('/seller/imports')).data,
  get: async (id) => (await api.get(`/seller/imports/${id}`)).data,
  upload: async (file, opts = {}) => { const fd = new FormData(); fd.append('file', file); return (await api.post('/seller/imports', fd, { params: opts, headers: { 'Content-Type': 'multipart/form-data' } })).data; },
  mapping: async (id, body) => (await api.put(`/seller/imports/${id}/mapping`, body)).data,
  confirm: async (id) => (await api.post(`/seller/imports/${id}/confirm`)).data,
  retry: async (id) => (await api.post(`/seller/imports/${id}/retry`)).data,
  remove: async (id) => (await api.delete(`/seller/imports/${id}`)).data,
  errorsCsv: async (id) => (await api.get(`/seller/imports/${id}/errors.csv`, { responseType: 'blob' })).data,
  profiles: async () => (await api.get('/seller/imports/profiles')).data,
  saveProfile: async (body) => (await api.post('/seller/imports/profiles', body)).data,
  deleteProfile: async (id) => (await api.delete(`/seller/imports/profiles/${id}`)).data,
};

export const feedsAPI = {
  options: async () => (await api.get('/seller/feeds/options')).data,
  list: async () => (await api.get('/seller/feeds')).data,
  create: async (body) => (await api.post('/seller/feeds', body)).data,
  update: async (id, body) => (await api.put(`/seller/feeds/${id}`, body)).data,
  remove: async (id) => (await api.delete(`/seller/feeds/${id}`)).data,
  sync: async (id) => (await api.post(`/seller/feeds/${id}/sync`, {}, { timeout: 150000 })).data,
  token: async (id) => (await api.post(`/seller/feeds/${id}/token`)).data,
  runs: async (id) => (await api.get(`/seller/feeds/${id}/runs`)).data,
  run: async (runId) => (await api.get(`/seller/feeds/runs/${runId}`)).data,
  runErrorsCsv: async (runId) => (await api.get(`/seller/feeds/runs/${runId}/errors.csv`, { responseType: 'blob' })).data,
  adminList: async (params) => (await api.get('/admin/feeds', { params })).data,
  adminGet: async (id) => (await api.get(`/admin/feeds/${id}`)).data,
  adminRun: async (runId) => (await api.get(`/admin/feeds/runs/${runId}`)).data,
  adminAction: async (id, action, reason) => (await api.post(`/admin/feeds/${id}/${action}`, { reason })).data,
};

export const mpShippingAPI = {
  options: async () => (await api.get('/mp/shipping/options')).data,
  sellerSettings: async () => (await api.get('/seller/mp/shipping-settings')).data,
  saveSellerSettings: async (body) => (await api.put('/seller/mp/shipping-settings', body)).data,
  sellerSubOrders: async (status) => (await api.get('/seller/mp/sub-orders', { params: { status } })).data,
  sellerSubOrder: async (id) => (await api.get(`/seller/mp/sub-orders/${id}`)).data,
  sellerAction: async (id, action, body = {}) => (await api.post(`/seller/mp/sub-orders/${id}/${action}`, body)).data,
  sellerUpdateShipping: async (id, body) => (await api.patch(`/seller/mp/sub-orders/${id}/shipping`, body)).data,
  sellerIncident: async (id, body) => (await api.post(`/seller/mp/sub-orders/${id}/shipping/incident`, body)).data,
  listingLogistics: async (id, body) => (await api.put(`/seller/listings/${id}/logistics`, body)).data,
  adminSettings: async () => (await api.get('/admin/mp/shipping-settings')).data,
  saveAdminSettings: async (body) => (await api.put('/admin/mp/shipping-settings', body)).data,
  adminShipments: async (params) => (await api.get('/admin/mp/shipments', { params })).data,
  adminShipment: async (id) => (await api.get(`/admin/mp/shipments/${id}`)).data,
  adminPatchShipping: async (id, body) => (await api.patch(`/admin/mp/shipments/${id}/shipping`, body)).data,
  adminIncident: async (id, body) => (await api.post(`/admin/mp/shipments/${id}/incident`, body)).data,
  adminSubAction: async (orderId, subId, action, body = {}) => (await api.post(`/admin/mp/orders/${orderId}/sub/${subId}/${action}`, body)).data,
};

export const mpCommerceAPI = {
  public: async () => (await api.get('/mp/commerce/public')).data,
  listingShipping: async (id) => (await api.get(`/occasion/listings/${id}/shipping`)).data,
  requestQuote: async (body) => (await api.post('/mp/shipping-quotes', body)).data,
  myQuotes: async () => (await api.get('/mp/shipping-quotes')).data,
  quoteAction: async (id, action) => (await api.post(`/mp/shipping-quotes/${id}/${action}`, {})).data,
  sellerQuotes: async () => (await api.get('/seller/mp/shipping-quotes')).data,
  sellerQuotePropose: async (id, body) => (await api.post(`/seller/mp/shipping-quotes/${id}/propose`, body)).data,
  sellerQuoteDecline: async (id, body) => (await api.post(`/seller/mp/shipping-quotes/${id}/decline`, body)).data,
  adminGet: async () => (await api.get('/admin/mp/commerce')).data,
  adminSave: async (body) => (await api.put('/admin/mp/commerce', body)).data,
  adminSettlements: async (status) => (await api.get('/admin/mp/settlements', { params: { status } })).data,
  adminQuotes: async () => (await api.get('/admin/mp/shipping-quotes')).data,
};

export const mpOrdersAPI = {
  list: async () => (await api.get('/mp/orders')).data,
  get: async (id) => (await api.get(`/mp/orders/${id}`)).data,
  delivered: async (id, subId) => (await api.post(`/mp/orders/${id}/sub/${subId}/delivered`, {})).data,
};

export const contactAPI = {
  subjects: async () => (await api.get('/contact/subjects')).data,
  send: async (data) => (await api.post('/contact', data)).data,
  list: async (status) => (await api.get('/admin/contact', { params: status ? { status } : {} })).data,
  inboxStatus: async () => (await api.get('/admin/inbox/status')).data,
  inboxSync: async () => (await api.post('/admin/inbox/sync')).data,
  update: async (id, data) => (await api.put(`/admin/contact/${id}`, data)).data,
  reply: async (id, message) => (await api.post(`/admin/contact/${id}/reply`, { message })).data,
  remove: async (id) => (await api.delete(`/admin/contact/${id}`)).data,
  partRequests: async (status) => (await api.get('/admin/part-requests', { params: status ? { status } : {} })).data,
  searchMisses: async (params) => (await api.get('/admin/search-misses', { params })).data,
  searchMissesCsv: async (days) => (await api.get('/admin/search-misses/export.csv', { params: { days }, responseType: 'blob' })).data,
  setPartRequestStatus: async (key, data) => (await api.put(`/admin/part-requests/${encodeURIComponent(key)}`, data)).data,
  linkPartRequest: async (key, productId) => (await api.post(`/admin/part-requests/${encodeURIComponent(key)}/link`, { product_id: productId })).data,
};

export const assistAPI = {
  createDiagnostic: async (formData) => (await api.post('/diagnostics', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 })).data,
  myDiagnostics: async () => (await api.get('/account/diagnostics')).data,
  diagnosticsQuota: async () => (await api.get('/account/diagnostics/quota')).data,
  notifications: async (params = {}) => (await api.get('/account/notifications', { params })).data,
  markRead: async () => (await api.post('/account/notifications/read')).data,
  setNotificationRead: async (id, read) => (await api.put(`/account/notifications/${id}`, { read })).data,
  deleteNotification: async (id) => (await api.delete(`/account/notifications/${id}`)).data,
  setNotifPrefs: async (prefs) => (await api.put('/auth/notif-prefs', { prefs })).data,
  maintenance: async () => (await api.get('/account/maintenance')).data,
  setMileage: async (vehicleId, mileage, yearlyKm) => (await api.put(`/account/vehicles/${vehicleId}/mileage`, { mileage, yearly_km: yearlyKm || null })).data,
  adminDiagnostics: async (status = '') => (await api.get(`/admin/diagnostics${status ? `?status=${status}` : ''}`)).data,
  resolveDiagnostic: async (id, data) => (await api.put(`/admin/diagnostics/${id}`, data)).data,
  rules: async () => (await api.get('/admin/maintenance/rules')).data,
  addRule: async (data) => (await api.post('/admin/maintenance/rules', data)).data,
  updateRule: async (id, data) => (await api.put(`/admin/maintenance/rules/${id}`, data)).data,
  deleteRule: async (id) => (await api.delete(`/admin/maintenance/rules/${id}`)).data,
  maintenanceStats: async () => (await api.get('/admin/maintenance/stats')).data,
  runMaintenance: async () => (await api.post('/admin/maintenance/run')).data,
};

export const accountAPI = {
  summary: async () => (await api.get('/account/summary')).data,
  addresses: async () => (await api.get('/account/addresses')).data,
  createAddress: async (data) => (await api.post('/account/addresses', data)).data,
  updateAddress: async (id, data) => (await api.put(`/account/addresses/${id}`, data)).data,
  setDefaultAddress: async (id, kind) => (await api.put(`/account/addresses/${id}/default?kind=${kind}`)).data,
  deleteAddress: async (id) => (await api.delete(`/account/addresses/${id}`)).data,
  bank: async () => (await api.get('/account/bank')).data,
  saveIban: async (data) => (await api.put('/account/bank/iban', data)).data,
  deleteIban: async () => (await api.delete('/account/bank/iban')).data,
  addCard: async (data) => (await api.post('/account/bank/cards', data)).data,
  defaultCard: async (id) => (await api.put(`/account/bank/cards/${id}/default`)).data,
  deleteCard: async (id) => (await api.delete(`/account/bank/cards/${id}`)).data,
  wallet: async () => (await api.get('/account/wallet')).data,
  proSectors: async () => (await api.get('/account/pro/sectors')).data,
  pro: async () => (await api.get('/account/pro')).data,
  requestPro: async (data) => (await api.post('/account/pro', data)).data,
  cancelPro: async () => (await api.delete('/account/pro')).data,
  preferences: async () => (await api.get('/account/preferences')).data,
  savePreferences: async (data) => (await api.put('/account/preferences', data)).data,
  deleteAccount: async (password) => (await api.post('/account/delete', { password })).data,
  adminWallet: async (userId) => (await api.get(`/admin/customers/${userId}/wallet`)).data,
  adminCredit: async (userId, amount, label) => (await api.post(`/admin/customers/${userId}/wallet`, { amount, label })).data,
  adminSendResetLink: async (userId) => (await api.post(`/admin/customers/${userId}/send-reset-link`)).data,
  adminUpdateCustomer: async (userId, data) => (await api.put(`/admin/customers/${userId}`, data)).data,
  adminDeleteCustomer: async (userId, force = false) => (await api.delete(`/admin/customers/${userId}${force ? '?force=true' : ''}`)).data,
  adminProDiscount: async (userId, pct) => (await api.put(`/admin/customers/${userId}/pro-discount`, { discount_pct: pct })).data,
  refundToWallet: async (orderId, amount, label = '') => (await api.post(`/orders/${orderId}/refund-to-wallet`, { amount, label })).data,
  adminReviewPro: async (userId, status, note = '') => (await api.put(`/admin/customers/${userId}/pro`, { status, note })).data,
};

export const historyAPI = {
  list: async (productId, limit = 50, field) => (await api.get(`/admin/price-history?limit=${limit}${productId ? `&product_id=${productId}` : ''}${field ? `&field=${field}` : ''}`)).data,
};

export const suppliersAPI = {
  importTemplate: () => downloadBlob('/admin/import-template.csv', 'modele-tarif-fournisseur.csv'),
  importPreview: async (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return (await api.post(`/admin/suppliers/${id}/import/preview`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },
  importApply: async (id, rows, updateSalePrice) => (await api.post(`/admin/suppliers/${id}/import/apply`, { rows, update_sale_price: updateSalePrice })).data,
  getAll: async () => (await api.get('/admin/suppliers')).data,
  getById: async (id) => (await api.get(`/admin/suppliers/${id}`)).data,
  create: async (data) => (await api.post('/admin/suppliers', data)).data,
  update: async (id, data) => (await api.put(`/admin/suppliers/${id}`, data)).data,
  delete: async (id) => (await api.delete(`/admin/suppliers/${id}`)).data,
  reorderSuggestions: async (id, threshold, target) => (await api.get(`/admin/suppliers/${id}/reorder-suggestions?target=${target}${threshold == null ? '' : `&threshold=${threshold}`}`)).data,
  purchaseOrders: async (id) => (await api.get(`/admin/suppliers/${id}/purchase-orders`)).data,
  createPurchaseOrder: async (id, data) => (await api.post(`/admin/suppliers/${id}/purchase-orders`, data)).data,
  receivePurchaseOrder: async (poId, lines = null, note = '') => (await api.put(`/admin/purchase-orders/${poId}/receive`, lines ? { lines, note } : {})).data,
  closePurchaseOrder: async (poId) => (await api.put(`/admin/purchase-orders/${poId}/close`)).data,
  remindPurchaseOrder: async (poId) => (await api.post(`/admin/purchase-orders/${poId}/remind`)).data,
  reschedulePurchaseOrder: async (poId, expectedDate, note = '') => (await api.put(`/admin/purchase-orders/${poId}/expected-date`, { expected_date: expectedDate, note })).data,
  latePurchaseOrders: async () => (await api.get('/admin/purchase-orders/late')).data,
  deletePurchaseOrder: async (poId) => (await api.delete(`/admin/purchase-orders/${poId}`)).data,
  validatePurchaseOrder: async (poId, data) => (await api.post(`/admin/purchase-orders/${poId}/validate`, data)).data,
  reorderPurchaseOrder: async (poId) => (await api.post(`/admin/purchase-orders/${poId}/reorder`)).data,
  stockForecast: async (productId) => (await api.get(`/admin/products/${productId}/stock-forecast`)).data,
  weeklyStockRecap: async () => (await api.get('/admin/stock/weekly-recap')).data,
  sendWeeklyStockRecap: async () => (await api.post('/admin/stock/weekly-recap/send')).data,
  downloadPurchaseOrder: async (po) => {
    const response = await api.get(`/admin/purchase-orders/${po.id}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bon-commande-${po.number}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
};

export const adminAPI = {
  stockNotifyRequests: async () => (await api.get('/admin/stock-notify/requests')).data,
  stockNotifyRun: async () => (await api.post('/admin/stock-notify/run')).data,
  stockNotifyFollowup: async (dryRun = false) => (await api.post(`/admin/stock-notify/followup?dry_run=${dryRun}`)).data,
  stockNotifySettings: async (data) => (await api.put('/admin/stock-notify/settings', data)).data,
  equivStats: async (days = 30, limit = 20) => (await api.get('/admin/equivalences/stats', { params: { days, limit } })).data,
  stockNotifyStats: async (weeks = 12) => (await api.get('/admin/stock-notify/stats', { params: { weeks } })).data,
  downloadEquivStatsCsv: async (days = 30) => {
    const response = await api.get('/admin/equivalences/stats.csv', { params: { days, limit: 100 }, responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url; a.download = `marques-equivalentes-${days}j-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  },
  categoryMatrix: async () => (await api.get('/admin/category-matrix')).data,
  applyCategoryMatrix: async (dryRun = true) => (await api.post(`/admin/products/apply-category-matrix?dry_run=${dryRun}`)).data,
  sumupRefund: async (orderId, data) => (await api.post(`/admin/orders/${orderId}/sumup-refund`, data)).data,
  searchProducts: async ({ q = '', page = 1, limit = 50, lowMargin = false, unpublished = false, seoGrade = '', sort = '', category = '', subcategory = '', vehicle = {}, noCompat = false, fuel = '' } = {}) => (await api.get('/admin/products/search', { params: { q, page, limit, low_margin: lowMargin, unpublished, seo_grade: seoGrade, sort, category, subcategory, vehicle_brand: vehicle.brand || '', vehicle_model: vehicle.model || '', vehicle_engine: vehicle.engine || '', no_compat: noCompat, fuel } })).data,
  bulkFuel: async (productIds, fuelTypes) => (await api.post('/admin/products/bulk-fuel', { product_ids: productIds, fuel_types: fuelTypes })).data,
  bulkAction: async (productIds, action) => (await api.post('/admin/products/bulk-action', { product_ids: productIds, action })).data,
  bulkPrice: async (productIds, percent, rounding, keepOriginal) => (await api.post('/admin/products/bulk-price', { product_ids: productIds, percent, rounding, keep_original: keepOriginal })).data,
  bulkStock: async (productIds, mode, value) => (await api.post('/admin/products/bulk-stock', { product_ids: productIds, mode, value })).data,
  reviewReward: async () => (await api.get('/admin/reviews/reward')).data,
  reviewAiSettings: async () => (await api.get('/admin/reviews/ai-settings')).data,
  saveReviewAiSettings: async (data) => (await api.put('/admin/reviews/ai-settings', data)).data,
  sendReviewsRecap: async () => (await api.post('/admin/reviews/weekly-recap/send')).data,
  runAutoThanks: async () => (await api.post('/admin/reviews/auto-thanks/run')).data,
  approveReview: async (reviewId) => (await api.put(`/admin/reviews/${reviewId}/approve`)).data,
  suggestReviewReply: async (reviewId) => (await api.post(`/admin/reviews/${reviewId}/suggest-reply`)).data,
  saveReviewReward: async (data) => (await api.put('/admin/reviews/reward', data)).data,
  bulkPriceOps: async (limit = 5) => (await api.get('/admin/products/bulk-price/ops', { params: { limit } })).data,
  undoBulkPriceOp: async (opId) => (await api.post(`/admin/products/bulk-price/ops/${opId}/undo`)).data,
  promos: async () => (await api.get('/admin/price-promos')).data,
  createPromo: async (payload) => (await api.post('/admin/price-promos', payload)).data,
  cancelPromo: async (id) => (await api.delete(`/admin/price-promos/${id}`)).data,
  exportSelection: async (productIds) => {
    const response = await api.post('/admin/products/export-selection.csv', { product_ids: productIds }, { responseType: 'blob' });
    const href = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = href; link.download = 'selection-produits.csv';
    document.body.appendChild(link); link.click(); link.remove();
    URL.revokeObjectURL(href);
  },
  improveProductsBulk: async () => (await api.post('/admin/products/improve-seo-bulk')).data,
  improveProductsStatus: async () => (await api.get('/admin/products/improve-seo-status')).data,
  seoDashboard: async () => (await api.get('/admin/seo/dashboard')).data,
  reviewCoverage: async () => (await api.get('/admin/reviews/coverage')).data,
  catalogPushStatus: async () => (await api.get('/admin/catalog/sync/push')).data,
  gscPerformance: async (days = 28, refresh = false) => (await api.get('/admin/seo/gsc/performance', { params: { days, refresh } })).data,
  catalogPushStart: async (payload) => (await api.post('/admin/catalog/sync/push', payload)).data,
  createManualReview: async (payload) => (await api.post('/admin/reviews/manual', payload)).data,
  manualReviewChannels: async () => (await api.get('/admin/reviews/manual/channels')).data,
  wooReviewsPreview: async (file) => { const fd = new FormData(); fd.append('file', file); return (await api.post('/admin/reviews/import/woo/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data; },
  wooReviewsApply: async (rows) => (await api.post('/admin/reviews/import/woo/apply', { rows, consent: true })).data,
  gscCheckDrop: async (force = false) => (await api.post('/admin/seo/gsc/check-drop', null, { params: { force } })).data,
  runReviewReminders: async () => (await api.post('/admin/reviews/reminders/run')).data,
  homeContent: async () => (await api.get('/admin/home-content')).data,
  saveHomeContent: async (payload) => (await api.put('/admin/home-content', payload)).data,
  resetHomeContent: async (lang, part) => (await api.delete('/admin/home-content', { params: { lang, part } })).data,
  translateHomeContent: async (langs) => (await api.post('/admin/home-content/translate', { langs }, { timeout: 300000 })).data,
  tyreSeasonPreview: async () => (await api.get('/admin/tyre-season/preview')).data,
  tyreSeasonSend: async (season, dryRun = false) => (await api.post('/admin/tyre-season/send', { season, dry_run: dryRun }, { timeout: 300000 })).data,
  unpaidHistory: async () => (await api.get('/admin/orders/unpaid-history')).data,
  seoUniqueness: async () => (await api.get('/admin/seo/uniqueness')).data,
  seoUniquenessRecompute: async () => (await api.post('/admin/seo/uniqueness/recompute', null, { timeout: 120000 })).data,
  unpaidPending: async () => (await api.get('/admin/orders/unpaid-pending')).data,
  cancelUnpaid: async (orderId = null) => (await api.post('/admin/orders/cancel-unpaid', null, { params: orderId ? { order_id: orderId } : {}, timeout: 120000 })).data,
  remindUnpaid: async () => (await api.post('/admin/orders/remind-unpaid', null, { timeout: 120000 })).data,
  downloadSeoReport: async (fmt) => {
    const response = await api.get(`/admin/seo/report.${fmt}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url; a.download = `rapport-seo-${new Date().toISOString().slice(0, 10)}.${fmt}`; a.click();
    window.URL.revokeObjectURL(url);
  },
  resendEmail: async (id) => (await api.post(`/admin/emails/${id}/resend`)).data,
  equivalentSuggestions: async (productId) => (await api.get(`/admin/products/${productId}/equivalent-suggestions`)).data,
  productAssist: async (payload) => (await api.post('/admin/products/assist', payload, { timeout: 180000 })).data,
  assistBatch: async (payload) => (await api.post('/admin/products/assist/batch', payload)).data,
  parseCompat: async (text) => (await api.post('/admin/products/compat/parse', { text })).data,
  bulkCompat: async (productIds, text, mode = 'merge') => (await api.post('/admin/products/compat/bulk-apply', { product_ids: productIds, text, mode })).data,
  compatUnmatched: async () => (await api.get('/admin/compat/unmatched')).data,
  seoPingStatus: async () => (await api.get('/admin/seo/ping')).data,
  seoPing: async (targets, sinceDays = 7) => (await api.post('/admin/seo/ping', { targets, since_days: sinceDays }, { timeout: 120000 })).data,
  seoPingSettings: async (patch) => (await api.put('/admin/seo/ping/settings', patch)).data,
  seoIndexation: async (level3Only = true) => (await api.get('/admin/seo/indexation', { params: { level3_only: level3Only } })).data,
  seoIndexationCheck: async (level3Only = true, limit = 150) => (await api.post('/admin/seo/indexation/check', { level3_only: level3Only, limit }, { timeout: 300000 })).data,
  seoIndexationSubmit: async (paths) => (await api.post('/admin/seo/indexation/submit', { paths }, { timeout: 120000 })).data,
  seoIndexationSettings: async (patch) => (await api.put('/admin/seo/indexation/settings', patch)).data,
  seoIndexationHistory: async (weeks = 26) => (await api.get('/admin/seo/indexation/history', { params: { weeks } })).data,
  seoIndexationDiagnose: async (paths, allProblems = false) => (await api.post('/admin/seo/indexation/diagnose', { paths, all_problems: allProblems }, { timeout: 300000 })).data,
  seoIndexationFix: async (path, code, target = null) => (await api.post('/admin/seo/indexation/fix', { path, code, target }, { timeout: 180000 })).data,
  seoIndexationFixPropose: async (path) => (await api.get('/admin/seo/indexation/fix/propose', { params: { path } })).data,
  prerenderCheck: async (target, lang = 'fr') => (await api.get('/admin/seo/prerender/check', { params: { ...(typeof target === 'string' ? { product: target } : target), lang } })).data,
  prerenderUrl: (path, lang = 'fr') => `${API}/prerender${path.startsWith('/') ? path : `/produit/${encodeURIComponent(path)}`}${lang !== 'fr' ? `?lang=${lang}` : ''}`,
  compatAiSuggest: async (payload) => (await api.post('/admin/products/compat/ai-suggest', payload)).data,
  compatAiComplete: async (productIds) => (await api.post('/admin/products/compat/ai-complete', { product_ids: productIds })).data,
  compatAiJob: async (jobId) => (await api.get(`/admin/products/compat/ai-complete/${jobId}`)).data,
  exportSearch: (format, { q = '', lowMargin = false, unpublished = false, seoGrade = '', sort = '', category = '', subcategory = '', vehicle = {}, noCompat = false, fuel = '' } = {}) => {
    const params = new URLSearchParams({ q, low_margin: lowMargin, unpublished, seo_grade: seoGrade, sort, category, subcategory, vehicle_brand: vehicle.brand || '', vehicle_model: vehicle.model || '', vehicle_engine: vehicle.engine || '', no_compat: noCompat, fuel });
    return downloadBlob(`/admin/products/search/export.${format}?${params.toString()}`, `produits-${new Date().toISOString().slice(0, 10)}.${format}`);
  },
  compatCreateVehicle: async (data) => (await api.post('/admin/compat/unmatched/create-vehicle', data)).data,
  compatLink: async (brand, model, targetModel) => (await api.post('/admin/compat/unmatched/link', { brand, model, target_model: targetModel })).data,
  compatDeleteUnmatched: async (brand, model) => (await api.delete('/admin/compat/unmatched', { params: { brand, model } })).data,
  assistBatches: async () => (await api.get('/admin/products/assist/batch')).data,
  generateProductDescriptions: async (productIds = null, limit = 10) => (await api.post('/admin/products/generate-descriptions', { product_ids: productIds, limit }, { timeout: 180000 })).data,
  generateProductSeo: async (productId, save = true) => (await api.post(`/admin/products/${productId}/generate-seo`, { save }, { timeout: 300000 })).data,
  productSeoCheck: async (productId) => (await api.get(`/admin/products/${productId}/seo-check`)).data,
  generateSeoBulk: async (overwrite = false, onlyCd = true) => (await api.post('/admin/products/generate-seo-bulk', { overwrite, only_cd: onlyCd })).data,
  generateSeoStatus: async () => (await api.get('/admin/products/generate-seo-status')).data,
  improveProductSeo: async (productId) => (await api.post(`/admin/products/${productId}/improve-seo`, null, { timeout: 240000 })).data,
  bulkCategories: async (productIds, category, subcategory, action = 'add') => (await api.post('/admin/products/bulk-categories', { product_ids: productIds, category, subcategory: subcategory || null, action })).data,
  setProductDescription: async (productId, description) => (await api.put(`/admin/products/${productId}/description`, { description })).data,
  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },
  getCustomers: async () => {
    const response = await api.get('/admin/customers');
    return response.data;
  },
  getReviews: async () => {
    const response = await api.get('/admin/reviews');
    return response.data;
  },
  replyReview: async (reviewId, message) => (await api.put(`/admin/reviews/${reviewId}/reply`, { message })).data,
  clearReviewReports: async (reviewId) => (await api.delete(`/admin/reviews/${reviewId}/reports`)).data,
  deleteReviewReply: async (reviewId) => (await api.delete(`/admin/reviews/${reviewId}/reply`)).data,
  setReviewVisibility: async (reviewId, hidden) => {
    const response = await api.put(`/admin/reviews/${reviewId}/visibility?hidden=${hidden}`);
    return response.data;
  },
  deleteReview: async (reviewId) => {
    const response = await api.delete(`/admin/reviews/${reviewId}`);
    return response.data;
  }
};

// Vehicles API
export const vehiclesAPI = {
  compareParams: (list, exact = true) => ({ ...Object.fromEntries(list.flatMap((v, i) => [[`${'abc'[i]}_brand`, v.brand], [`${'abc'[i]}_model`, v.model], ...(v.engine ? [[`${'abc'[i]}_engine`, v.engine]] : [])])), ...(exact ? {} : { exact: 'false' }) }),
  comparePdfUrl: (list, exact = true) => `${API}/vehicles/compare/pdf?${new URLSearchParams(vehiclesAPI.compareParams(list, exact)).toString()}`,
  compare: async (list, exact = true) => (await api.get('/vehicles/compare', { params: vehiclesAPI.compareParams(list, exact) })).data,
  packPromo: async (list, exact = true) => (await api.post('/vehicles/compare/pack-promo', { ...vehiclesAPI.compareParams(list), exact })).data,
  packShare: async (list, exact, payload) => (await api.post('/vehicles/compare/pack-share', { ...vehiclesAPI.compareParams(list), exact, ...payload })).data,
  garageBudget: async (exact = true) => (await api.get('/account/garage-budget', { params: { exact } })).data,
  garageAdditives: async (vehicleId) => (await api.get('/account/garage-additives', { params: vehicleId ? { vehicle_id: vehicleId } : {} })).data,
  setReplaced: async (vehicleId, postKey, replacedAt, mileage, currentMileage) => (await api.put(`/account/vehicles/${vehicleId}/maintenance/${postKey}`, { replaced_at: replacedAt || null, mileage: mileage ?? null, current_mileage: currentMileage ?? null })).data,
  clearReplaced: async (vehicleId, postKey) => (await api.delete(`/account/vehicles/${vehicleId}/maintenance/${postKey}`)).data,
  packShareStats: async (limit = 20) => (await api.get('/admin/pack-shares/stats', { params: { limit } })).data,
  trackShare: (payload) => fetch(`${API}/vehicles/track-share`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), keepalive: true }).catch(() => {}),
  budgetAlerts: async (limit = 20) => (await api.get('/admin/budget-alerts', { params: { limit } })).data,
  runBudgetAlerts: async (params = {}) => (await api.post('/admin/budget-alerts/run', null, { params })).data,
  myBudgetAlerts: async () => (await api.get('/account/budget-alerts')).data,
  budgetHistory: async (vehicleId, months = 12) => (await api.get(`/account/vehicles/${vehicleId}/budget-history`, { params: { months } })).data,
  modelBudget: async (slug, modelSlug) => (await api.get(`/vehicles/brand-pages/${encodeURIComponent(slug)}/${encodeURIComponent(modelSlug)}/budget`)).data,
  budgetRanking: async (brand) => (await api.get('/vehicles/budget-ranking', { params: brand ? { brand } : {} })).data,
  kmView: async (token) => (await api.get(`/km/${encodeURIComponent(token)}`)).data,
  kmSave: async (token, mileage, yearlyKm) => (await api.post(`/km/${encodeURIComponent(token)}`, { mileage, yearly_km: yearlyKm || null })).data,
  carnetEmail: async (vehicleId) => (await api.post(`/account/vehicles/${vehicleId}/carnet-email`)).data,
  carnetPdf: async (vehicleId) => {
    const res = await api.get(`/account/vehicles/${vehicleId}/carnet.pdf`, { responseType: 'blob' });
    const name = (res.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'carnet-entretien.pdf';
    const url = URL.createObjectURL(res.data);
    const a = Object.assign(document.createElement('a'), { href: url, download: name });
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    return name;
  },
  alertSubscribe: async (brandSlug, modelSlug, email, lang, frequency = 'weekly', engine = null) => (await api.post('/vehicles/alerts', { brand_slug: brandSlug, model_slug: modelSlug, email: email || null, lang, frequency, engine: engine || null })).data,
  alertStatus: async (brandSlug, modelSlug, engine = null) => (await api.get('/vehicles/alerts/status', { params: { brand_slug: brandSlug, model_slug: modelSlug, engine: engine || undefined } })).data,
  alertRemove: async (brandSlug, modelSlug, engine = null) => (await api.delete('/vehicles/alerts', { params: { brand_slug: brandSlug, model_slug: modelSlug, engine: engine || undefined } })).data,
  getBrandEngines: async (brand, models = []) => {
    const q = models.length ? `?models=${encodeURIComponent(models.join('|'))}` : '';
    return (await api.get(`/vehicles/brand-engines/${encodeURIComponent(brand)}${q}`)).data;
  },

  getBrands: async () => {
    const response = await api.get('/vehicles/brands');
    return response.data.brands;
  },
  getBrandLogos: async () => {
    const response = await api.get('/vehicles/brands');
    return response.data.logos || {};
  },
  getBrandsFull: async () => (await api.get('/vehicles/brands')).data,
  getBrandPages: async () => (await api.get('/vehicles/brand-pages')).data,
  getBrandPage: async (slug) => (await api.get(`/vehicles/brand-pages/${encodeURIComponent(slug)}`)).data,
  getBrandProducts: async (slug, category, model, engine) => (await api.get(`/vehicles/brand-parts/${encodeURIComponent(slug)}`, { params: { category: category || undefined, model: model || undefined, engine: engine || undefined, limit: 24 } })).data,
  getModelPage: async (slug, modelSlug) => (await api.get(`/vehicles/brand-pages/${encodeURIComponent(slug)}/${encodeURIComponent(modelSlug)}`)).data,
  
  getModels: async (brand) => {
    const response = await api.get(`/vehicles/models/${encodeURIComponent(brand)}`);
    return response.data.models;
  },
  getModelImages: async (brand) => (await api.get(`/vehicles/model-images/${encodeURIComponent(brand)}`)).data.images || {},
  requestModelImage: async (brand, model) => (await api.post(`/vehicles/${encodeURIComponent(brand)}/${encodeURIComponent(model)}/image-request`)).data,
  modelImageStatus: async (brand, model) => (await api.get(`/vehicles/${encodeURIComponent(brand)}/${encodeURIComponent(model)}/image-status`)).data,
  
  getYears: async (brand, model) => {
    const response = await api.get(`/vehicles/years/${encodeURIComponent(brand)}/${encodeURIComponent(model)}`);
    return response.data.years;
  },
  
  getEngines: async (brand, model, year) => {
    const response = await api.get(`/vehicles/engines/${encodeURIComponent(brand)}/${year}/${encodeURIComponent(model)}`);
    return response.data.engines;
  },

  lookupPlate: async (plate) => {
    const response = await api.get(`/vehicles/plate/${encodeURIComponent(plate)}`);
    return response.data;
  }
};

// Admin Plates API
export const adminPlatesAPI = {
  getAll: async () => {
    const response = await api.get('/admin/plates');
    return response.data;
  },
  register: async (plateData) => {
    const response = await api.post('/admin/plates', plateData);
    return response.data;
  },
  delete: async (plate) => {
    const response = await api.delete(`/admin/plates/${encodeURIComponent(plate)}`);
    return response.data;
  },
  setStatus: async (plate, status) => (await api.put(`/admin/plates/${encodeURIComponent(plate)}/status?status=${status}`)).data,
};

export const adminVehicleModelSeoAPI = {
  list: async (brand) => (await api.get('/admin/vehicle-models/seo', { params: { brand } })).data,
  get: async (brand, model) => (await api.get(`/admin/vehicle-models/seo/${encodeURIComponent(brand)}/${encodeURIComponent(model)}`)).data,
  update: async (brand, model, data) => (await api.put(`/admin/vehicle-models/seo/${encodeURIComponent(brand)}/${encodeURIComponent(model)}`, data)).data,
  generate: async (brand, model) => (await api.post(`/admin/vehicle-models/seo/${encodeURIComponent(brand)}/${encodeURIComponent(model)}/generate`, {}, { timeout: 120000 })).data,
  remove: async (brand, model) => (await api.delete(`/admin/vehicle-models/seo/${encodeURIComponent(brand)}/${encodeURIComponent(model)}`)).data,
  translate: async (brand, model) => (await api.post(`/admin/vehicle-models/seo/${encodeURIComponent(brand)}/${encodeURIComponent(model)}/translate`, {}, { timeout: 180000 })).data,
  generateAll: async (brand = null, { overwrite = false, limit = 50, translate = true, mode = 'generate' } = {}) => (await api.post('/admin/vehicle-models/seo-generate-all', { brand, overwrite, limit, translate, mode })).data,
  job: async (jobId) => (await api.get(`/admin/vehicle-models/seo-jobs/${jobId}`)).data,
  popular: async (limit = 50) => (await api.get('/admin/vehicle-models/seo-popular', { params: { limit } })).data,
  auto: async () => (await api.get('/admin/vehicle-models/seo-auto')).data,
  setAuto: async (data) => (await api.put('/admin/vehicle-models/seo-auto', data)).data,
};

export const adminSumupAPI = {
  events: async (kind, limit = 100) => (await api.get('/admin/sumup/events', { params: { kind: kind || undefined, limit } })).data,
  pending: async (live = true) => (await api.get('/admin/sumup/pending', { params: { live } })).data,
  refresh: async () => (await api.post('/admin/sumup/refresh')).data,
};

export const adminCodeHealthAPI = {
  get: async () => (await api.get('/admin/code-health')).data,
  run: async (tests = true) => (await api.post(`/admin/code-health/run?tests=${tests}`)).data,
  suites: async () => (await api.get('/admin/code-health/suites')).data,
  suite: async (key) => (await api.get(`/admin/code-health/suites/${key}`)).data,
  runSuite: async (key) => (await api.post(`/admin/code-health/suites/${key}/run`)).data,
};

export const adminSeoAuditAPI = {
  emptyPages: async () => (await api.get('/admin/seo/empty-pages')).data,
  orphanLinks: async () => (await api.get('/admin/seo/orphan-links')).data,
};

export const adminVehicleBrandsAPI = {
  list: async () => (await api.get('/admin/vehicle-brands')).data,
  shareStats: async (top = 10) => (await api.get('/admin/vehicle-brands/share-stats', { params: { top } })).data,
  shareAttribution: async (days = 90, limit = 20) => (await api.get('/admin/vehicle-brands/share-attribution', { params: { days, limit } })).data,
  referralStats: async () => (await api.get('/admin/share-referral/stats')).data,
  referralLeaderboard: async (limit = 10) => (await api.get('/admin/share-referral/leaderboard', { params: { limit } })).data,
  referralRemind: async (dryRun = false) => (await api.post(`/admin/share-referral/remind?dry_run=${dryRun}`)).data,
  referralSettings: async () => (await api.get('/admin/share-referral/settings')).data,
  saveReferralSettings: async (data) => (await api.put('/admin/share-referral/settings', data)).data,
  create: async (names, fetchLogos = true) => (await api.post('/admin/vehicle-brands', { names, fetch_logos: fetchLogos })).data,
  update: async (name, data) => (await api.put(`/admin/vehicle-brands/${encodeURIComponent(name)}`, data)).data,
  remove: async (name, force = false) => (await api.delete(`/admin/vehicle-brands/${encodeURIComponent(name)}?force=${force}`)).data,
  purge: async () => (await api.post('/admin/vehicles/purge?confirm=SUPPRIMER')).data,
  fetchLogos: async () => (await api.post('/admin/vehicle-brands/fetch-logos', {}, { timeout: 300000 })).data,
  setPopular: async (names) => (await api.put('/admin/vehicle-brands/popular', { names })).data,
  generateSeo: async (name) => (await api.post(`/admin/vehicle-brands/${encodeURIComponent(name)}/generate-seo`)).data,
  generateAllSeo: async () => (await api.post('/admin/vehicle-brands/generate-all', {}, { timeout: 300000 })).data,
  seoCheck: async (name) => (await api.get(`/admin/vehicle-brands/${encodeURIComponent(name)}/seo-check`)).data,
  translateSeo: async (name, overwrite = false) => (await api.post(`/admin/vehicle-brands/${encodeURIComponent(name)}/translate-seo`, { overwrite }, { timeout: 180000 })).data,
  translateAllSeo: async (overwrite = false, limit = null) => (await api.post('/admin/vehicle-brands/translate-all', { overwrite, limit })).data,
  seoI18nStatus: async () => (await api.get('/admin/vehicle-brands/seo-i18n-status')).data,
  importModelsText: async (brand, text, replace = false) => (await api.post('/admin/vehicles/import-text', { brand, text, replace })).data,
  importEnginesText: async (text, replaceEngines = false, target = null) => (await api.post('/admin/vehicles/import-engines-text', { text, replace_engines: replaceEngines, brand: target?.brand, model: target?.model })).data,
};

// Admin Vehicles API
export const adminVehiclesAPI = {
  aiImagesStatus: async () => (await api.get('/admin/vehicle-images/ai/status')).data,
  aiImagesGenerate: async (data) => (await api.post('/admin/vehicle-images/ai/generate', data)).data,
  aiImagesSettings: async (data) => (await api.put('/admin/vehicle-images/ai/settings', data)).data,
  aiImagesClearQueue: async () => (await api.delete('/admin/vehicle-images/ai/queue')).data,
  aiImageRegenerate: async (brand, model) => (await api.post('/admin/vehicle-images/ai/regenerate', { brand, model }, { timeout: 120000 })).data,
  getAll: async () => {
    const response = await api.get('/admin/vehicles');
    return response.data;
  },
  
  getDetails: async (brand, model) => {
    const response = await api.get(`/admin/vehicles/${encodeURIComponent(brand)}/${encodeURIComponent(model)}`);
    return response.data;
  },
  uploadImage: async (brand, model, file) => { const fd = new FormData(); fd.append('file', file); return (await api.post(`/admin/vehicles/${encodeURIComponent(brand)}/${encodeURIComponent(model)}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 })).data; },
  deleteImage: async (brand, model) => (await api.delete(`/admin/vehicle-images/${encodeURIComponent(brand)}/${encodeURIComponent(model)}`)).data,
  
  add: async (vehicleData) => {
    const response = await api.post('/admin/vehicles', vehicleData);
    return response.data;
  },
  
  update: async (brand, model, vehicleData) => {
    const response = await api.put(`/admin/vehicles/${encodeURIComponent(brand)}/${encodeURIComponent(model)}`, vehicleData);
    return response.data;
  },
  
  delete: async (brand, model) => {
    const response = await api.delete(`/admin/vehicles/${encodeURIComponent(brand)}/${encodeURIComponent(model)}`);
    return response.data;
  },
  
  seedVehicles: async () => {
    const response = await api.post('/seed-vehicles');
    return response.data;
  },
  
  importVehicles: async (vehicles, mode = 'add') => {
    const response = await api.post('/admin/vehicles/import', { vehicles, mode });
    return response.data;
  },
  
  exportVehicles: async () => {
    const response = await api.get('/admin/vehicles/export');
    return response.data;
  },
  exportVehiclesCsv: () => downloadBlob('/admin/vehicles/export.csv', `vehicules-otopieces-${new Date().toISOString().slice(0, 10)}.csv`),
};

// Articles API
export const articlesAPI = {
  rewrite: async (id) => (await api.post(`/admin/articles/${id}/rewrite`, {}, { timeout: 180000 })).data,
  rewrites: async (status = 'pending') => (await api.get('/admin/articles/rewrites', { params: { status } })).data,
  rewritesRun: async () => (await api.post('/admin/articles/rewrites/run', {}, { timeout: 300000 })).data,
  rewriteApply: async (id) => (await api.post(`/admin/articles/rewrites/${id}/apply`)).data,
  rewriteReject: async (id) => (await api.post(`/admin/articles/rewrites/${id}/reject`)).data,
  // Fired on page exit via sendBeacon so the request survives navigation
  engagement: (id, seconds, depth) => { try { navigator.sendBeacon(`${API}/articles/${id}/engagement`, new Blob([JSON.stringify({ seconds, depth })], { type: 'application/json' })); } catch { /* ignore */ } },
  top: async (limit = 3) => (await api.get('/articles/top', { params: { limit } })).data,
  getAll: async () => {
    const response = await api.get('/articles');
    return response.data;
  },
  
  adminList: async () => (await api.get('/admin/articles')).data,
  create: async (data) => (await api.post('/admin/articles', data)).data,
  update: async (id, data) => (await api.put(`/admin/articles/${id}`, data)).data,
  delete: async (id) => (await api.delete(`/admin/articles/${id}`)).data,
  getById: async (id) => {
    const response = await api.get(`/articles/${id}`);
    return response.data;
  }
};

export const competitorsAPI = {
  getOverview: async (position = null, sort = 'gap') => (await api.get('/admin/competitors/overview', { params: { position: position || undefined, sort } })).data,
  getProduct: async (productId) => (await api.get(`/admin/competitors/products/${productId}`)).data,
  setUrls: async (productId, urls) => (await api.put(`/admin/competitors/products/${productId}/urls`, { urls })).data,
  setManualPrices: async (productId, prices) => (await api.post(`/admin/competitors/products/${productId}/manual-prices`, { prices })).data,
  run: async (productId = null) => (await api.post('/admin/competitors/run', { product_id: productId })).data,
  latestRun: async () => (await api.get('/admin/competitors/runs/latest')).data,
  alignPreview: async (productId, offset = 0.1) => (await api.get(`/admin/competitors/products/${productId}/align-preview`, { params: { offset } })).data,
  align: async (productId, offset = 0.1) => (await api.post(`/admin/competitors/products/${productId}/align`, { offset })).data,
  alignPreviewAll: async (offset = 0.1) => (await api.get('/admin/competitors/align-preview-all', { params: { offset } })).data,
  alignAll: async (productIds, offset = 0.1) => (await api.post('/admin/competitors/align-all', { product_ids: productIds, offset })).data,
  getSettings: async () => (await api.get('/admin/competitors/settings')).data,
  scraperUsage: async () => (await api.get('/admin/competitors/scraper-usage')).data,
  setSettings: async (minMarginPct) => (await api.put('/admin/competitors/settings', { min_margin_pct: minMarginPct })).data,
  setConnection: async (payload) => (await api.put('/admin/competitors/settings', payload)).data,
  testConnection: async (url) => (await api.post('/admin/competitors/settings/test-connection', { url })).data,
  getTyres: async () => (await api.get('/admin/competitors/tyres')).data,
  setTyreSources: async (sources) => (await api.put('/admin/competitors/tyres/sources', { sources })).data,
  assignTyres: async (overwrite = false) => (await api.post('/admin/competitors/tyres/assign', { overwrite })).data,
  setAutoAlign: async (productId, autoAlign, offset = 0.1) => (await api.put(`/admin/competitors/products/${productId}/auto-align`, { auto_align: autoAlign, auto_align_offset: offset })).data,
  exportCsv: async (position = null) => {
    const response = await api.get('/admin/competitors/export.csv', { params: { position: position || undefined }, responseType: 'blob' });
    const name = (response.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'veille-tarifaire.csv';
    const url = URL.createObjectURL(response.data);
    const a = Object.assign(document.createElement('a'), { href: url, download: name });
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    return name;
  },
};

export default api;
